<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <!-- Top bar -->
        <div class="page-bar py-3">
          <div class="container">
            <div class="d-flex align-items-center">
              <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                <i class="fas fa-chevron-left"></i>
              </BackButton>
              <div class="page-title flex-grow-1">Enquiries</div>
            </div>
          </div>
        </div>

        <!-- Helper box -->
        <div class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded">
          <p class="text-dark mb-2">Vendors who are hired for this event will appear here.</p>
          <div class="mt-2">
            <router-link :to="`/add-enquiry/${eventId}`" class="btn btn-outline-success btn-sm">
              <i class="fas fa-plus mr-1"></i> Add Enquiry
            </router-link>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center p-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <div class="mt-2 text-muted small">Fetching enquiries…</div>
        </div>

        <!-- Vendors list -->
        <div v-else-if="enquiries.length > 0" class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded">
          <div class="card mb-3" v-for="enquiry in enquiries" :key="enquiry.id || enquiry.s_user_id">
            <div class="card-body">
              <div class="d-flex flex-column flex-md-row">
                <!-- Image -->
                <div class="mb-3 mb-md-0 me-md-3 flex-shrink-0">
                  <img
                    :src="enquiry.s_user_img || 'https://dummyimage.com/200x200/222/fff.png&text=V'"
                    :alt="enquiry.s_business || 'Vendor'"
                    class="img-fluid rounded"
                    style="max-width: 120px; width: 100%; height: auto;"
                  />
                </div>

                <!-- Content -->
                <div class="flex-grow-1 pl-2">
                  <h4 class="h5 mb-2">{{ enquiry.s_business }}</h4>

                  <div class="mb-2">
                    <p class="mb-1 small">
                      <i class="fas fa-shapes mr-1 text-primary"></i>
                      <span class="text-muted">{{ enquiry.contextual_categories }}</span>
                    </p>
                    <p class="mb-1 small">
                      <i class="fas fa-calendar mr-1 text-primary"></i>
                      <span class="text-muted">
                        {{ formatDate(enquiry.created_at) }}
                      </span>
                    </p>
                  </div>

                  <p class="small mb-0">
                    <i class="fas fa-message mr-1 text-primary"></i>
                    {{ enquiry.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded text-center">
          <p class="py-2">
            There are no enquiries for this event yet. You can add vendors by selecting <strong>Add Enquiry</strong>.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import { useHead } from '@vueuse/head'
import { useRoute } from 'vue-router'
import { formatDisplayDate } from '@/utils/date'
import BackButton from '@/components/common/BackButton.vue'

useHead({
  title: 'Enquiries - Invi8',
  meta: [
    { name: 'description', content: 'View and manage your event enquiries.' },
    { name: 'keywords', content: 'enquiries, vendors, events, invites' },
  ],
})

const session = useSessionStore()
const route = useRoute()

const loading = ref(false)
const enquiries = ref([])
const eventId = ref(deriveEventId(route))

function deriveEventId(r) {
  const cand = [r.params?.eventid, r.params?.id, r.params?.slug, r.params?.eventId, r.query?.eventid, r.query?.id, r.query?.eventId]
    .find(v => v != null && String(v).trim() !== '')
  if (cand) return String(cand)
  const lastSeg = (r.path || r.fullPath || '').split('?')[0].split('/').filter(Boolean).pop()
  return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null
}

const formatDate = (val) => formatDisplayDate(val, { options: { month: 'short', day: '2-digit', year: 'numeric' } })

async function getEnquiries() {
  loading.value = true
  try {
    const fd = new FormData()
    fd.append('userid', session.userId || '')
    if (eventId.value) {
      // send both keys to be safe with the backend
      fd.append('eventid', eventId.value)
      fd.append('event_id', eventId.value)
    }
    const res = await api.getEnquiries(fd)
    const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
    const list = ok ? (res.data?.list || res.data || []) : []
    enquiries.value = Array.isArray(list) ? list : []
  } catch (e) {
    console.error('getEnquiries error:', e)
    enquiries.value = []
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await session.loadUser()
  await getEnquiries()
})

// Re-derive event id and reload when route changes
watch(() => route.fullPath, async () => {
  const next = deriveEventId(route)
  if (next !== eventId.value) {
    eventId.value = next
  }
  await getEnquiries()
})
</script>

<style scoped>
@media (max-width: 400px) {
  .card-body { padding: 1rem; }
  .flex-wrap p { font-size: 0.85rem; }
}
</style>
